import React, { useState } from 'react';
import {
  Button,
  Dialog,
  Divider,
  DownloadButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Flex,
  ListItem,
} from '@/components/ui';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useDownloadFile } from '@/hooks/useDownloadFile';
import { PERMISSIONS } from '@/types/permissions';
import { getStudyFileDownloadEndpoint } from '@/utils/downloadEndpoints';

import {
  IconDots,
  IconDownload,
  IconEye,
  IconHistory,
  IconLoader,
  IconTrash,
} from '@tabler/icons-react';

import { StudyFileActionsProps } from './types';

const StudyFileActions: React.FC<StudyFileActionsProps> = ({
  file,
  onDelete,
  onShowVersions,
  onPreview,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const canDeleteFiles = usePermissionAccess([PERMISSIONS.STUDY_FILES_DELETE]);
  const canManageFiles = usePermissionAccess([PERMISSIONS.STUDY_FILES_ACCESS]);

  const { downloadFromApi, isLoading: isDownloading } = useDownloadFile({
    showToast: true,
    successMessage: `Downloaded ${file.filename}`,
    errorMessage: 'Failed to download file',
  });

  const handleDeleteConfirm = () => {
    onDelete(file);
    setDeleteDialogOpen(false);
  };

  const handleShowVersions = () => {
    onShowVersions(file);
  };

  const handlePreview = () => {
    if (onPreview && canManageFiles) {
      onPreview(file);
    }
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    await downloadFromApi(getStudyFileDownloadEndpoint(file), file.filename);
  };

  return (
    <Flex gap="1" align="center">
      {/* Download button */}
      <DownloadButton
        apiEndpoint={getStudyFileDownloadEndpoint(file)}
        filename={file.filename}
        variant="text"
        size="small"
        isIconOnly
        title="Download file"
        successMessage={`Downloaded ${file.filename}`}
        errorMessage="Failed to download file"
      />

      {/* Preview button - only show if user has manage files permission */}
      {canManageFiles && onPreview && (
        <Button
          variant="text"
          size="small"
          isIconOnly
          onClick={handlePreview}
          startIcon={<IconEye />}
          title="Preview file"
        />
      )}

      {/* Versions history button */}
      <Button
        variant="text"
        size="small"
        isIconOnly
        onClick={handleShowVersions}
        startIcon={<IconHistory />}
        title="View version history"
      />

      {/* Delete button with confirmation dialog - only show if user has delete permission */}
      {canDeleteFiles && (
        <Dialog.Root open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <Dialog.Trigger>
            <Button
              variant="text"
              size="small"
              color="error"
              isIconOnly
              startIcon={<IconTrash />}
              title="Delete file"
            />
          </Dialog.Trigger>
          <Dialog.Content size="medium">
            <Dialog.Body>
              <Dialog.Title>Delete File</Dialog.Title>
              <Dialog.Description>
                Are you sure you want to delete{' '}
                <strong>"{file.filename}"</strong>? This action cannot be
                undone.
              </Dialog.Description>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.Close>
                <Button variant="outlined" color="secondary">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                variant="classic"
                color="error"
                onClick={handleDeleteConfirm}
              >
                Delete
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Root>
      )}

      {/* More actions dropdown (for future use) */}
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            variant="text"
            size="small"
            isIconOnly
            startIcon={<IconDots />}
            title="More actions"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent variant="soft" color="gray">
          <DropdownMenuItem asChild>
            <ListItem
              startIcon={
                isDownloading ? (
                  <IconLoader className="animate-spin" />
                ) : (
                  <IconDownload />
                )
              }
              primary="Download"
              onClick={handleDownload}
              disabled={isDownloading}
            />
          </DropdownMenuItem>
          {canManageFiles && onPreview && (
            <DropdownMenuItem asChild>
              <ListItem
                startIcon={<IconEye />}
                primary="Preview File"
                onClick={handlePreview}
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <ListItem
              startIcon={<IconHistory />}
              primary="Version History"
              onClick={handleShowVersions}
            />
          </DropdownMenuItem>
          <Divider />
          {canDeleteFiles && (
            <DropdownMenuItem asChild>
              <ListItem
                startIcon={<IconTrash />}
                primary="Delete"
                onClick={() => setDeleteDialogOpen(true)}
                className="text-red-600"
              />
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </Flex>
  );
};

export default StudyFileActions;
